import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts, parseLocalDate } from '../data/blog-posts';
import { POST_IMAGES } from '../data/post-assets';
import { AdUnit } from '../components/AdUnit';
import './Blog.css';

function readingTime(content: string): number {
  return Math.max(1, Math.round(content.trim().split(/\s+/).length / 200));
}

type SortOrder = 'newest' | 'oldest';

export function Blog() {
  const [search, setSearch]               = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');
  const [sortOrder, setSortOrder]         = useState<SortOrder>('newest');

  // All unique series names, sorted alphabetically
  const allSeries = useMemo(() => {
    const names = new Set<string>();
    blogPosts.forEach(p => { if (p.series) names.add(p.series.name); });
    return Array.from(names).sort();
  }, []);

  const isFiltering = !!(search.trim() || selectedSeries);

  // Apply series → search → sort
  const filteredAndSorted = useMemo(() => {
    let posts = [...blogPosts];

    if (selectedSeries) {
      posts = posts.filter(p => p.series?.name === selectedSeries);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      posts = posts.filter(p =>
        p.title.toLowerCase().includes(q) ||
        p.excerpt.toLowerCase().includes(q) ||
        p.tags.some(tag => tag.toLowerCase().includes(q))
      );
    }

    if (sortOrder === 'oldest') {
      posts.reverse();
    }

    return posts;
  }, [search, selectedSeries, sortOrder]);

  // Default view: featured hero + rest of posts newest-first
  // Filtered view: flat sorted grid, no featured split
  const featured  = !isFiltering ? blogPosts[0] : undefined;
  const gridPosts = isFiltering  ? filteredAndSorted : blogPosts.slice(1);

  const clearAll = () => { setSearch(''); setSelectedSeries(''); setSortOrder('newest'); };

  // Human-readable result summary
  const resultSummary = useMemo(() => {
    const count = filteredAndSorted.length;
    const noun  = count === 1 ? 'essay' : 'essays';
    const parts: string[] = [`${count} ${noun}`];
    if (selectedSeries) parts.push(`in ${selectedSeries}`);
    if (search.trim())  parts.push(`matching "${search.trim()}"`);
    return parts.join(' ');
  }, [filteredAndSorted.length, selectedSeries, search]);

  return (
    <div className="blog-page">

      {/* Editorial Masthead */}
      <div className="blog-page__masthead">
        <div className="container">
          <div className="blog-page__masthead-inner">
            <span className="blog-page__masthead-kicker">Notation of Being</span>
            <h1 className="blog-page__masthead-heading">The Blog</h1>
            <p className="blog-page__masthead-sub">
              Reflections on presence, authorship, and the frames we inherit
            </p>
            <div className="blog-page__masthead-rule" />
          </div>
        </div>
      </div>

      <div className="blog-page__body">
        <div className="container">

          {/* Filter + Search row */}
          <div className="blog-page__toolbar">

            {/* Left: filter dropdowns */}
            <div className="blog-page__filters">

              {/* Series filter */}
              <div className="blog-page__select-wrap">
                <select
                  className="blog-page__filter-select"
                  value={selectedSeries}
                  onChange={e => setSelectedSeries(e.target.value)}
                  aria-label="Filter by series"
                >
                  <option value="">All Series</option>
                  {allSeries.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
                <i className="fas fa-chevron-down blog-page__select-chevron" aria-hidden="true" />
              </div>

              {/* Sort order */}
              <div className="blog-page__select-wrap">
                <select
                  className="blog-page__filter-select"
                  value={sortOrder}
                  onChange={e => setSortOrder(e.target.value as SortOrder)}
                  aria-label="Sort order"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </select>
                <i className="fas fa-chevron-down blog-page__select-chevron" aria-hidden="true" />
              </div>

              {/* Clear all — only when any filter is active */}
              {isFiltering && (
                <button className="blog-page__filter-clear" onClick={clearAll} aria-label="Clear all filters">
                  <i className="fas fa-times" /> Clear
                </button>
              )}
            </div>

            {/* Right: search */}
            <div className="blog-page__search">
              <i className="fas fa-search blog-page__search-icon" />
              <input
                type="text"
                className="blog-page__search-input"
                placeholder="Search essays…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search blog posts"
              />
              {search && (
                <button
                  className="blog-page__search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  <i className="fas fa-times" />
                </button>
              )}
            </div>
          </div>

          {/* Result count — only when filtering */}
          {isFiltering && filteredAndSorted.length > 0 && (
            <p className="blog-page__result-count">{resultSummary}</p>
          )}

          {/* Featured (latest) post — only in default browse mode */}
          {featured && (
            <Link to={`/blog/${featured.id}`} className="blog-page__featured">
              {featured.image && POST_IMAGES[featured.image] && (
                <div className="blog-page__featured-image-wrap">
                  <img
                    src={POST_IMAGES[featured.image]}
                    alt={featured.title}
                    className="blog-page__featured-image"
                  />
                </div>
              )}
              <div className="blog-page__featured-content">
                <div className="blog-page__featured-eyebrow">
                  <span className="blog-page__featured-badge">Latest Essay</span>
                  <span className="blog-page__featured-read">
                    {readingTime(featured.content)} min read
                  </span>
                </div>

                {featured.series && (
                  <div className="blog-page__series-badge">
                    <i className="fas fa-layer-group" />
                    {featured.series.name} &middot; No.&nbsp;{featured.series.part}
                  </div>
                )}

                <h2 className="blog-page__featured-title">{featured.title}</h2>
                <p className="blog-page__featured-excerpt">{featured.excerpt}</p>

                <div className="blog-page__featured-meta">
                  <span>{featured.author}</span>
                  <span className="blog-page__meta-dot" aria-hidden="true">·</span>
                  <span>
                    {parseLocalDate(featured.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                </div>

                <div className="blog-page__featured-tags">
                  {featured.tags.slice(0, 3).map(tag => (
                    <span key={tag} className="blog-page__tag">{tag}</span>
                  ))}
                </div>

                <span className="blog-page__featured-cta">
                  Read Essay <i className="fas fa-arrow-right" />
                </span>
              </div>
            </Link>
          )}

          {/* Horizontal ad — between featured and grid, default browse only */}
          {featured && (
            <div className="blog-page__ad-wrap blog-page__ad-wrap--between">
              <AdUnit slot="6240382661" format="auto" />
            </div>
          )}

          {/* Grid — additional posts or filter results */}
          {gridPosts.length > 0 && (
            <>
              {!isFiltering && (
                <div className="blog-page__section-divider"><span>More Essays</span></div>
              )}
              {isFiltering && selectedSeries && !search && (
                <div className="blog-page__section-divider">
                  <span>{selectedSeries}</span>
                </div>
              )}
              <div className="blog-page__grid">
                {gridPosts.flatMap((post, i) => {
                  const card = (
                    <Link key={post.id} to={`/blog/${post.id}`} className="blog-page__card">
                      {post.image && POST_IMAGES[post.image] && (
                        <div className="blog-page__card-image-wrap">
                          <img
                            src={POST_IMAGES[post.image]}
                            alt={post.title}
                            className="blog-page__card-image"
                          />
                        </div>
                      )}
                      <div className="blog-page__card-content">
                        <div className="blog-page__card-tags">
                          {post.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="blog-page__tag">{tag}</span>
                          ))}
                        </div>
                        {post.series && (
                          <div className="blog-page__series-badge blog-page__series-badge--card">
                            {post.series.name} &middot; No.&nbsp;{post.series.part}
                          </div>
                        )}
                        <h3 className="blog-page__card-title">{post.title}</h3>
                        <p className="blog-page__card-excerpt">{post.excerpt}</p>
                        <div className="blog-page__card-meta">
                          <span>{parseLocalDate(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                          <span className="blog-page__meta-dot" aria-hidden="true">·</span>
                          <span>{readingTime(post.content)} min read</span>
                        </div>
                      </div>
                    </Link>
                  );
                  // Square ad after 2nd card, only in default browse mode
                  if (!isFiltering && i === 1 && gridPosts.length >= 2) {
                    return [
                      card,
                      <div key="ad-square" className="blog-page__ad-item">
                        <AdUnit slot="6723957185" format="auto" />
                      </div>,
                    ];
                  }
                  return [card];
                })}
              </div>
            </>
          )}

          {/* Horizontal ad — below grid when filtering */}
          {isFiltering && filteredAndSorted.length > 0 && (
            <div className="blog-page__ad-wrap blog-page__ad-wrap--between">
              <AdUnit slot="6240382661" format="auto" />
            </div>
          )}

          {/* Empty state */}
          {isFiltering && filteredAndSorted.length === 0 && (
            <div className="blog-page__empty">
              <i className="fas fa-layer-group" />
              <h3>No essays found</h3>
              <p>
                {selectedSeries && search
                  ? `Nothing matched "${search}" in ${selectedSeries}`
                  : selectedSeries
                  ? `No essays in ${selectedSeries} yet`
                  : `No essays matched "${search}"`}
              </p>
              <button className="blog-page__empty-clear" onClick={clearAll}>
                Clear filters
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
